import { useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ProfileRevenuesSection } from './profile-revenues-section';
import { ProfileCertificationsSection } from './profile-certifications-section';
import { InterventionZonesSection } from './intervention-zones-section';
import { CommercialStrategySection } from './commercial-strategy-section';
import { AggregatedReferencesSection } from './aggregated-references-section';
import {
  useDismissOnboarding,
  useOnboardingStatus,
  useProfile,
} from '../hooks/use-dossier-company';

/**
 * Sprint 5b.1 v2 — Phase D.1 : wizard d'onboarding en 6 étapes.
 *
 * Affiché au premier login d'un `tenant_admin` (déclenché par OnboardingTrigger).
 * Re-déclenchable manuellement depuis la page profil sans reset du timestamp DB
 * (cf. décision actée #23).
 *
 * « Quitter » et « Terminer » posent tous deux `onboardingCompanyDossierAt = now()`.
 */

interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
}

const STEPS: Array<{
  title: string;
  description: string;
  Component: () => JSX.Element;
}> = [
  {
    title: 'Identité de l\'entreprise',
    description: 'Vérifiez vos données SIRENE (lecture seule) et complétez l\'adresse de correspondance si nécessaire.',
    Component: IdentityStep,
  },
  {
    title: 'Chiffres clés',
    description: 'Effectif total, effectif technique et chiffre d\'affaires pluriannuel.',
    Component: KeyFiguresStep,
  },
  {
    title: 'Certifications',
    description: 'Vos certifications principales (ISO, Qualiopi, etc.).',
    Component: CertificationsStep,
  },
  {
    title: 'Zones d\'intervention',
    description: 'Où vous intervenez géographiquement. Au moins une zone PRIMARY recommandée.',
    Component: ZonesStep,
  },
  {
    title: 'Stratégie commerciale',
    description: 'Extensions et exclusions volontaires de votre périmètre métier automatique + montants AO ciblés.',
    Component: StrategyStep,
  },
  {
    title: 'Références agrégées',
    description: 'Vue d\'ensemble de votre activité AO (facultatif).',
    Component: ReferencesStep,
  },
];

export function OnboardingWizard({ open, onClose }: OnboardingWizardProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const dismiss = useDismissOnboarding();
  const { data: status } = useOnboardingStatus();

  const totalSteps = STEPS.length;
  const currentStep = STEPS[stepIndex];
  const isLastStep = stepIndex === totalSteps - 1;
  const isFirstStep = stepIndex === 0;

  // Décision #23 : le timestamp est un marqueur historique « première vue ». N'est jamais
  // réinitialisé. Le re-déclenchement manuel n'appelle PAS dismiss à la fermeture.
  const alreadyDismissed = status?.onboardingCompanyDossierAt !== null && status?.onboardingCompanyDossierAt !== undefined;

  async function handleClose() {
    if (!alreadyDismissed) {
      try {
        await dismiss.mutateAsync();
      } catch {
        // tolérant : on ferme même si la mutation échoue (sera ré-essayé au prochain login)
      }
    }
    setStepIndex(0);
    onClose();
  }

  if (!currentStep) return null;
  const StepComponent = currentStep.Component;
  const progressPercent = Math.round(((stepIndex + 1) / totalSteps) * 100);

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? handleClose() : null)}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">
                  Étape {stepIndex + 1}/{totalSteps}
                </span>
                <span>{currentStep.title}</span>
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {currentStep.description}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleClose()}
              disabled={dismiss.isPending}
            >
              <X className="h-4 w-4 mr-1" />
              Quitter
            </Button>
          </div>
          <Progress value={progressPercent} className="mt-3" />
        </DialogHeader>

        <div className="py-2">
          <StepComponent />
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 border-t">
          <Button
            variant="outline"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={isFirstStep}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Précédent
          </Button>

          <div className="flex items-center gap-2">
            {!isLastStep && (
              <Button
                variant="ghost"
                onClick={() => setStepIndex((i) => Math.min(totalSteps - 1, i + 1))}
              >
                Passer cette étape
              </Button>
            )}
            {isLastStep ? (
              <Button onClick={() => handleClose()} disabled={dismiss.isPending}>
                {dismiss.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Terminer
              </Button>
            ) : (
              <Button
                onClick={() => setStepIndex((i) => Math.min(totalSteps - 1, i + 1))}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sous-composants par étape ───

function IdentityStep() {
  const { data } = useProfile();
  const c = data?.company;
  if (!c) {
    return (
      <p className="text-sm text-muted-foreground">
        Pas encore de données SIRENE. Rendez-vous sur la page profil pour lancer un refresh.
      </p>
    );
  }
  return (
    <div className="text-sm space-y-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-muted-foreground text-xs">SIRET</p>
          <p className="font-mono">{c.siret ?? '—'}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Dénomination</p>
          <p className="font-medium">{c.denomination ?? '—'}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Catégorie juridique</p>
          <p>{c.categorieJuridiqueLabel ?? '—'}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Adresse siège</p>
          <p>
            {[c.siegeAdresseRue, c.siegeCodePostal, c.siegeCommune].filter(Boolean).join(', ') || '—'}
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground italic pt-2">
        Pour modifier ces informations, utilisez « Rafraîchir SIRENE » depuis la page profil.
      </p>
    </div>
  );
}

function KeyFiguresStep() {
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Renseignez ici votre chiffre d'affaires sur les 3 dernières années (utilisé par la checklist GO).
      </p>
      <ProfileRevenuesSection />
    </div>
  );
}

function CertificationsStep() {
  return <ProfileCertificationsSection />;
}

function ZonesStep() {
  return <InterventionZonesSection />;
}

function StrategyStep() {
  return <CommercialStrategySection />;
}

function ReferencesStep() {
  return <AggregatedReferencesSection />;
}
